// Requests that waited too long for a decision (FR-8.5).
//
// The nightly sweep (20261015170000_escalations.sql) files one row per request
// left pending past policy.escalation.afterDays working days and tells the
// approvers and their managers. The row closes itself when the request is
// decided or withdrawn. esc_read is people:full; nobody writes it from here.
import { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';

export type Escalation = {
  id: string;
  subject_type: 'leave' | 'correction';
  subject_id: string;
  profile_id: string;
  filed_on: string;
  escalated_at: string;
  resolved_at: string | null;
  resolution: 'approved' | 'rejected' | 'withdrawn' | null;
  full_name: string | null;
};

/** How far back a closed escalation stays on the card. */
const KEEP_DAYS = 60;

export const listEscalations = async (): Promise<Escalation[]> => {
  if (!supabase) return [];
  const since = new Date(Date.now() - KEEP_DAYS * 86_400_000).toISOString();
  const { data } = await supabase
    .from('escalations')
    .select(
      'id, subject_type, subject_id, profile_id, filed_on, escalated_at, resolved_at, resolution, profiles(full_name)',
    )
    .or(`resolved_at.is.null,resolved_at.gte.${since}`)
    .order('escalated_at', { ascending: false });
  type Row = Omit<Escalation, 'full_name'> & {
    profiles: { full_name: string | null } | { full_name: string | null }[] | null;
  };
  return ((data as Row[] | null) ?? []).map(({ profiles, ...r }) => ({
    ...r,
    full_name: (Array.isArray(profiles) ? profiles[0] : profiles)?.full_name ?? null,
  }));
};

/** A leave request or correction as the queue beside the card holds it. */
type Live = { id: string; status: string; decided_at: string | null };

/** Closes each open escalation whose request the queue already shows decided. */
export const closeDecided = (rows: Escalation[], live: readonly Live[]): Escalation[] => {
  const decided = new Map(
    live.filter((r) => r.status === 'approved' || r.status === 'rejected').map((r) => [r.id, r]),
  );
  return rows.map((e) => {
    const d = !e.resolved_at && decided.get(e.subject_id);
    return d
      ? {
          ...e,
          resolution: d.status as 'approved' | 'rejected',
          resolved_at: d.decided_at ?? new Date().toISOString(),
        }
      : e;
  });
};

/**
 * HR's escalations, closed as soon as the queue beside them decides one. A
 * decision there is optimistic, so a refetch on it would race the write and read
 * the row still open; the queue's own status is the live answer, and the row
 * itself catches up on the next load. Nothing for anyone but the deciders.
 */
export const useEscalations = (
  canDecide: boolean,
  reqs: readonly Live[],
  corrections: readonly Live[] | null,
): Escalation[] => {
  const [rows, setRows] = useState<Escalation[]>([]);
  useEffect(() => {
    if (!canDecide) return;
    let live = true;
    void listEscalations().then((r) => live && setRows(r));
    return () => {
      live = false;
    };
  }, [canDecide]);
  return useMemo(
    () => closeDecided(rows, [...reqs, ...(corrections ?? [])]),
    [rows, reqs, corrections],
  );
};
